import { put, call, spawn, takeLatest } from 'redux-saga/effects'

import * as types from 'syft-acp-core/actions/action-types'
import { showDesktopNotification } from 'syft-acp-core/lib/notifications'
import {
  requestNotificationPermission,
  enableNotificationsGloballyIfUnset,
  setDesktopNotifications,
} from 'syft-acp-core/actions/notifications'
import { store } from 'syft-acp-core/store'

// Display a single desktop notification.
const displayNotification = action => {
  showDesktopNotification(action.payload)
}

// Check for permissions if we are enabling desktop notifications.
export function* checkPermissions({ enabled }) {
  if (enabled) {
    yield put(requestNotificationPermission())
  } else {
    yield put(setDesktopNotifications(false))
  }
}

const setRequestNotificationPermission = async () => {
  // Disregard on devices that do not support notifications.
  if (!window.Notification) return

  // Caught here so a rejected prompt can never abort the saga that started this: the detached
  // startup task, or the DESKTOP_NOTIFICATIONS_REQ_PERMISSION watcher, whose error would otherwise
  // bubble to the root saga and stop every watcher in it.
  try {
    if (Notification.permission === 'granted') {
      // If we've already received permission, set to true right away.
      store.dispatch(setDesktopNotifications(true))
    } else {
      // Otherwise, ask for permission.
      const permission = (await Notification.requestPermission()) === 'granted'
      store.dispatch(setDesktopNotifications(permission))
    }
  } catch {
    // A prompt that failed granted nothing, so treat it as a refusal.
    store.dispatch(setDesktopNotifications(false))
  }
}

const enableNotificationsGloballyIfUnsetFn = () => {
  // Turn on notifications if unset (i.e. the user hasn't decided yet).
  store.dispatch(enableNotificationsGloballyIfUnset())
}

export default function* root() {
  // On startup, request permission to use desktop notifications. Spawned, not called: the root saga
  // registers modules in sequence, and Chrome's quiet prompt leaves the promise pending until the
  // user clicks it, which held back every saga registered after this one. Spawned rather than
  // forked so the prompt is detached: nothing it does can cancel or fail the root saga.
  yield spawn(setRequestNotificationPermission)
  yield call(enableNotificationsGloballyIfUnsetFn)
  yield takeLatest(types.DESKTOP_NOTIFICATION_SHOW, displayNotification)
  yield takeLatest(types.DESKTOP_NOTIFICATIONS_TOGGLE, checkPermissions)
  yield takeLatest(types.DESKTOP_NOTIFICATIONS_REQ_PERMISSION, setRequestNotificationPermission)
}
